/*
 * OzMobiles: light gallery for Apple Watch listings (snippets/oz-watch-gallery.liquid).
 *
 * - When the variant changes, the main photo becomes that variant's photo (same srcset widths as Liquid).
 * - When the condition or the case finish changes, the thumbnails are rebuilt for the new finish, one per
 *   band in the Band Colour option's order, from the product JSON (oz-high-variants.js completes it past
 *   250 variants and fires oz:product-json-updated, which rebuilds them again).
 * - A thumbnail click selects that band through the theme's own swatch, so price, stock, add to cart and
 *   the address bar follow the theme as before.
 *
 * Speed: the CDN takes 1 to 1.5 s to make a size of a photo the first time anyone asks for it, then
 * answers in about 15 ms. So the photo switches at once to its thumbnail (already loaded) and the sharp
 * one replaces it as soon as it has decoded; hovering or pressing a thumbnail or swatch starts loading
 * that photo before the click; and the srcset keeps to three widths so more requests hit the cache.
 */
(function () {
  'use strict';

  // Keep in step with snippets/oz-watch-gallery.liquid.
  var WIDTHS = [600, 900, 1200];
  var THUMB_WIDTH = 144;

  function productData(section) {
    var el = section.querySelector('[data-product-json]');
    try { return el ? JSON.parse(el.textContent || el.innerHTML) : null; } catch (e) { return null; }
  }

  // Shopify's CDN resizes by the width parameter.
  function sized(src, width) {
    var url = new URL(src, window.location.href);
    url.searchParams.set('width', String(width));
    return url.toString().replace(/^https?:/, '');
  }

  function photoOf(variant) {
    var media = variant && variant.featured_media;
    return media && media.preview_image ? media : null;
  }

  function Gallery(section, list) {
    this.section = section;
    this.list = list;
    this.image = section.querySelector('.oz-watch-gallery__image');
    this.bandPosition = parseInt(list.getAttribute('data-oz-band-position'), 10);
    this.key = null;

    list.addEventListener('click', this.onThumbnail.bind(this));
    var onIntent = this.onIntent.bind(this);
    section.addEventListener('mouseover', onIntent);
    section.addEventListener('pointerdown', onIntent);
  }

  // Everything chosen except the band, which is what the thumbnails vary.
  Gallery.prototype.selection = function () {
    var self = this;
    var chosen = {};
    this.section.querySelectorAll('.product-form__single-selector:checked').forEach(function (input) {
      var position = parseInt(input.getAttribute('data-option-position'), 10);
      if (position !== self.bandPosition) { chosen['option' + position] = input.value; }
    });
    return chosen;
  };

  function srcsetOf(media) {
    var src = media.preview_image.src;
    return WIDTHS.map(function (w) { return sized(src, w) + ' ' + w + 'w'; }).join(', ');
  }

  // Start loading a photo at the size the main image would pick, so a click finds it ready.
  Gallery.prototype.preload = function (variant) {
    var media = photoOf(variant);
    if (!media || !this.image) { return null; }
    this.preloaded = this.preloaded || {};
    if (!this.preloaded[media.id]) {
      var loader = new Image();
      loader.sizes = this.image.getAttribute('sizes') || '';
      loader.srcset = srcsetOf(media);
      loader.src = sized(media.preview_image.src, WIDTHS[WIDTHS.length - 1]);
      this.preloaded[media.id] = loader.decode ? loader.decode().then(function () { return true; }, function () { return false; }) : Promise.resolve(true);
    }
    return this.preloaded[media.id];
  };

  Gallery.prototype.showPhoto = function (variant) {
    var media = photoOf(variant);
    if (!this.image || !media || this.image.getAttribute('data-oz-media-id') === String(media.id)) { return; }
    var image = this.image;
    var id = String(media.id);
    image.setAttribute('alt', media.alt || '');
    image.setAttribute('data-oz-media-id', id);

    // At once: the thumbnail of the same photo, already in the browser.
    var thumb = this.list.querySelector('.product-gallery__thumbnail[data-media-id="' + id + '"] img');
    image.removeAttribute('srcset');
    image.setAttribute('src', thumb ? thumb.currentSrc || thumb.src : sized(media.preview_image.src, THUMB_WIDTH));

    // Then the sharp photo, once decoded, unless another variant was picked meanwhile.
    this.preload(variant).then(function () {
      if (image.getAttribute('data-oz-media-id') !== id) { return; }
      image.setAttribute('srcset', srcsetOf(media));
      image.setAttribute('src', sized(media.preview_image.src, WIDTHS[WIDTHS.length - 1]));
    });
  };

  // The variant the current choices would give with one option changed.
  Gallery.prototype.variantWith = function (position, value) {
    var data = productData(this.section);
    if (!data || !data.product) { return null; }
    var chosen = {};
    this.section.querySelectorAll('.product-form__single-selector:checked').forEach(function (input) {
      chosen['option' + input.getAttribute('data-option-position')] = input.value;
    });
    chosen['option' + position] = value;
    return data.product.variants.filter(function (v) {
      return Object.keys(chosen).every(function (k) { return v[k] === chosen[k]; });
    })[0] || null;
  };

  Gallery.prototype.onIntent = function (event) {
    var thumb = event.target.closest && event.target.closest('.product-gallery__thumbnail');
    if (thumb && this.list.contains(thumb)) {
      this.preload(this.variantWith(this.bandPosition, thumb.getAttribute('data-oz-band')));
      return;
    }
    var label = event.target.closest && event.target.closest('label.color-swatch__item');
    var input = label && document.getElementById(label.getAttribute('for'));
    if (input && this.section.contains(input)) {
      this.preload(this.variantWith(input.getAttribute('data-option-position'), input.value));
    }
  };

  Gallery.prototype.markSelected = function (variant) {
    var band = variant ? variant['option' + this.bandPosition] : null;
    this.list.querySelectorAll('.product-gallery__thumbnail').forEach(function (thumb) {
      thumb.classList.toggle('is-nav-selected', thumb.getAttribute('data-oz-band') === band);
    });
    this.revealSelected();
  };

  // Bring the selected thumbnail to the middle of the strip, scrolling only the strip itself (a column
  // on desktop, a row on phones), never the page.
  Gallery.prototype.revealSelected = function () {
    var thumb = this.list.querySelector('.is-nav-selected');
    if (!thumb) { return; }
    [this.list, this.list.parentElement].forEach(function (box) {
      var boxRect = box.getBoundingClientRect();
      var rect = thumb.getBoundingClientRect();
      if (box.scrollHeight > box.clientHeight) {
        box.scrollTop += (rect.top + rect.height / 2) - (boxRect.top + box.clientHeight / 2);
      }
      if (box.scrollWidth > box.clientWidth) {
        box.scrollLeft += (rect.left + rect.width / 2) - (boxRect.left + box.clientWidth / 2);
      }
    });
  };

  // Rebuild the thumbnails when anything but the band changed since they were drawn.
  Gallery.prototype.rebuild = function (force) {
    var chosen = this.selection();
    var key = JSON.stringify(chosen);
    if (!force && key === this.key) { return; }
    this.key = key;

    var data = productData(this.section);
    if (!data || !data.product) { return; }
    var bandOption = (data.options_with_values || [])[this.bandPosition - 1];
    if (!bandOption) { return; }

    var byBand = {};
    data.product.variants.forEach(function (variant) {
      var matches = Object.keys(chosen).every(function (k) { return variant[k] === chosen[k]; });
      if (matches && photoOf(variant)) { byBand[variant['option' + this.bandPosition]] = variant; }
    }, this);

    var fragment = document.createDocumentFragment();
    bandOption.values.forEach(function (band) {
      var variant = byBand[band];
      if (!variant) { return; }
      var link = document.createElement('a');
      link.className = 'product-gallery__thumbnail';
      link.href = window.location.pathname + '?variant=' + variant.id;
      link.title = band;
      link.setAttribute('data-oz-band', band);
      link.setAttribute('data-media-id', String(variant.featured_media.id));
      var box = document.createElement('div');
      box.className = 'aspect-ratio';
      box.style.paddingBottom = '100%';
      var img = document.createElement('img');
      img.loading = 'lazy';
      img.width = 72;
      img.height = 72;
      img.alt = band;
      img.src = sized(variant.featured_media.preview_image.src, THUMB_WIDTH);
      box.appendChild(img);
      link.appendChild(box);
      fragment.appendChild(link);
    });
    // Keep the server's list when the JSON has none of this selection yet (the first second of a
    // listing past 250 variants); oz:product-json-updated rebuilds it once the rest is in.
    if (!fragment.childNodes.length) { this.key = null; return; }
    this.list.replaceChildren(fragment);
  };

  Gallery.prototype.onThumbnail = function (event) {
    var thumb = event.target.closest('.product-gallery__thumbnail');
    if (!thumb) { return; }
    event.preventDefault();
    var band = thumb.getAttribute('data-oz-band');
    var position = this.bandPosition;
    var input = Array.prototype.filter.call(
      this.section.querySelectorAll('.product-form__single-selector[data-option-position="' + position + '"]'),
      function (candidate) { return candidate.value === band; }
    )[0];
    if (!input || input.checked) { return; }
    var label = this.section.querySelector('label[for="' + input.id + '"]');
    (label || input).click();
  };

  Gallery.prototype.update = function (variant, force) {
    this.rebuild(force);
    this.showPhoto(variant);
    this.markSelected(variant);
  };

  var galleries = [];

  function currentVariant(section) {
    var data = productData(section);
    if (!data || !data.product) { return null; }
    var id = data.selected_variant_id;
    var select = section.querySelector('select[name="id"]');
    if (select && select.value) { id = parseInt(select.value, 10); }
    return data.product.variants.filter(function (v) { return v.id === id; })[0] || null;
  }

  function setUp() {
    galleries = [];
    document.querySelectorAll('[data-section-type="product"]').forEach(function (section) {
      var list = section.querySelector('[data-oz-watch-gallery]');
      if (!list) { return; }
      var gallery = new Gallery(section, list);
      gallery.key = JSON.stringify(gallery.selection());
      gallery.revealSelected();
      galleries.push(gallery);
    });
  }

  document.addEventListener('variant:changed', function (event) {
    galleries.forEach(function (gallery) {
      if (gallery.section.contains(event.target) || event.target.contains(gallery.section)) {
        gallery.update(event.detail.variant, false);
      }
    });
  });

  document.addEventListener('oz:product-json-updated', function () {
    galleries.forEach(function (gallery) { gallery.update(currentVariant(gallery.section), true); });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setUp);
  } else {
    setUp();
  }
})();
